import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Building2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Field, StatusBadge } from "@/components/common";

const EMPTY = { name: "", gstin: "", city: "", state: "", phone: "", owner_name: "", owner_email: "", owner_password: "" };

export default function Platform() {
  const { setActAs } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const load = () => api.get("/platform/companies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const create = async () => {
    try { await api.post("/platform/companies", f); toast.success("Company created"); setOpen(false); setF(EMPTY); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (c, v) => {
    try { await api.patch(`/platform/companies/${c.id}`, { active: v }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const enter = (c) => { setActAs({ id: c.id, name: c.name }); navigate("/dashboard"); };
  const sum = (k) => items.reduce((s, c) => s + c.stats[k], 0);

  return (
    <div>
      <PageHeader title="Transport Companies" subtitle="Platform console · manage every tenant from one place">
        <Button data-testid="add-company-btn" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" />New Company</Button>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="platform-companies" label="Companies" value={items.length} icon={Building2} />
        <StatCard testid="platform-vehicles" label="Vehicles" value={sum("vehicles")} />
        <StatCard testid="platform-bookings" label="Bookings" value={sum("bookings")} />
        <StatCard testid="platform-revenue" label="Total Freight" value={inr(sum("revenue"))} accent="text-emerald-600" />
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((c, i) => (
          <div key={c.id} data-testid={`company-card-${c.id}`} className="card-base p-5 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-lg font-bold font-display">{c.name}</div>
                <div className="text-xs text-muted-foreground">{[c.city, c.state].filter(Boolean).join(", ") || "—"} {c.gstin && `· ${c.gstin}`}</div>
              </div>
              <StatusBadge status={c.active ? "Active" : "Disabled"} />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              {[["Vehicles", c.stats.vehicles], ["Drivers", c.stats.drivers], ["LRs", c.stats.bookings], ["Users", c.stats.users]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-secondary/60 py-2"><div className="amount text-lg font-semibold">{v}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div></div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch data-testid={`company-active-${c.id}`} checked={c.active} onCheckedChange={(v) => toggle(c, v)} /> Active</label>
              <Button size="sm" data-testid={`open-company-${c.id}`} disabled={!c.active} onClick={() => enter(c)}>Open <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-card" data-testid="company-dialog">
          <DialogHeader><DialogTitle>New Transport Company</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Company Name" className="col-span-2"><Input data-testid="new-company-name-input" value={f.name} onChange={set("name")} /></Field>
            <Field label="GSTIN"><Input data-testid="new-company-gstin-input" value={f.gstin} onChange={set("gstin")} /></Field>
            <Field label="Phone"><Input data-testid="new-company-phone-input" value={f.phone} onChange={set("phone")} /></Field>
            <Field label="City"><Input data-testid="new-company-city-input" value={f.city} onChange={set("city")} /></Field>
            <Field label="State"><Input data-testid="new-company-state-input" value={f.state} onChange={set("state")} /></Field>
            <Field label="Owner Name"><Input data-testid="new-company-owner-name-input" value={f.owner_name} onChange={set("owner_name")} /></Field>
            <Field label="Owner Email"><Input data-testid="new-company-owner-email-input" type="email" value={f.owner_email} onChange={set("owner_email")} /></Field>
            <Field label="Owner Password" className="col-span-2"><Input data-testid="new-company-owner-password-input" value={f.owner_password} onChange={set("owner_password")} placeholder="Min 6 characters" /></Field>
          </div>
          <DialogFooter><Button data-testid="new-company-save-btn" onClick={create}>Create Company</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
